<template>
  <div class="fts-teammate">
    <header class="tm-header">
      <div class="tm-header-row">
        <div class="tm-title">
          <font-awesome-icon :icon="['fas', 'wand-magic-sparkles']" />
          <span>AI Teammate</span>
          <span class="tm-tag">test bench</span>
        </div>

        <div class="tm-pickers">
          <div class="tm-picker tm-combo" ref="wsCombo"
            :title="selectedWorkspaceId ? `Sent as wsId header: ${selectedWorkspaceId}` : 'Select a workspace — sent as wsId HTTP header'">
            <font-awesome-icon class="tm-picker-icon" :icon="['far', 'building']" />
            <input
              ref="wsInput"
              class="tm-picker-select tm-combo-input"
              type="text"
              autocomplete="off"
              spellcheck="false"
              :value="wsOpen ? wsSearch : selectedWorkspaceName"
              :placeholder="loadingWorkspaces ? 'Loading…' : '— Select workspace —'"
              :disabled="loadingWorkspaces"
              @focus="openWsDropdown"
              @input="onWsSearchInput"
              @keydown="onWsKeydown"
            />
            <font-awesome-icon
              v-if="loadingWorkspaces || wsSearching"
              class="tm-picker-spin"
              :icon="['fas', 'spinner']"
              spin
            />
            <font-awesome-icon
              v-else
              class="tm-combo-caret"
              :icon="['fas', wsOpen ? 'caret-up' : 'caret-down']"
              @mousedown.prevent="toggleWsDropdown"
            />
            <ul v-if="wsOpen" ref="wsMenu" class="tm-combo-menu">
              <li v-if="!filteredWorkspaces.length" class="tm-combo-empty">
                {{ wsSearching ? 'Searching…' : 'No matching workspaces' }}
              </li>
              <li
                v-for="(ws, idx) in filteredWorkspaces"
                :key="ws.id"
                :class="['tm-combo-option', {
                  'tm-combo-option--active': idx === wsActiveIndex,
                  'tm-combo-option--selected': isWsSelected(ws),
                }]"
                @mousedown.prevent="selectWorkspace(ws)"
                @mouseenter="wsActiveIndex = idx"
              >
                {{ ws.name || ws.id }}
              </li>
            </ul>
          </div>

          <div class="tm-picker"
            :title="selectedModuleName ? `Module: ${selectedModuleName}` : 'No module — module-required actions are disabled'">
            <font-awesome-icon class="tm-picker-icon" :icon="['fas', 'cubes']" />
            <select class="tm-picker-select" v-model="selectedModuleName" :disabled="loadingModules || !selectedWorkspaceId">
              <option value="">— No module —</option>
              <option v-for="m in modules" :key="m.id || m.name" :value="m.name">{{ m.name }}</option>
            </select>
            <font-awesome-icon v-if="loadingModules" class="tm-picker-spin" :icon="['fas', 'spinner']" spin />
          </div>

          <!-- Model override — only when the server provider is OpenRouter -->
          <div v-if="meta.modelSelectable" class="tm-picker tm-picker--model"
            :title="modelOverride ? `Model override: ${modelOverride}` : `Server default: ${meta.defaultModel || 'configured model'}`">
            <font-awesome-icon class="tm-picker-icon" :icon="['fas', 'wand-sparkles']" />
            <input class="tm-picker-select" type="text" list="tm-model-options"
              v-model.trim="modelOverride" :disabled="inflight"
              :placeholder="meta.defaultModel || 'model id (server default)'"
              spellcheck="false" autocomplete="off" />
            <datalist id="tm-model-options">
              <option v-for="m in modelOptions" :key="m" :value="m" />
            </datalist>
          </div>
        </div>
      </div>

      <nav class="tm-tabs">
        <button v-for="tab in tabs" :key="tab.id" type="button"
          :class="['tm-tab', { 'tm-tab--active': activeTab === tab.id }]"
          @click="activeTab = tab.id">
          {{ tab.label }}
        </button>
      </nav>
    </header>

    <main class="tm-body">
      <!-- ====== CHAT / CONVERSE (shared chat UI; /chat is the unified agent) ====== -->
      <section v-if="isChatTab" class="tm-pane tm-pane--chat">
        <div class="tm-chat-config">
          <div class="tm-field tm-field-inline">
            <label>Entry ID</label>
            <input type="text" v-model.trim="activeChat.entryId" placeholder="(optional)" />
          </div>
          <label v-if="activeTab === 'chat'" class="tm-chat-design"
            title="Expose the design_module / modify_module tools. Honoured only for Administrator / Workspace Admin accounts.">
            <input type="checkbox" v-model="chat.design" :disabled="inflight" />
            <font-awesome-icon :icon="['fas', 'pen-ruler']" />
            Design tools
          </label>
          <span v-if="activeTab === 'chat'" class="tm-chat-note">
            {{ chat.design
              ? 'Agent can also propose module designs/changes (admins only).'
              : 'Answers questions and runs entry activities.' }}
          </span>
          <div v-if="chatTotals.total > 0" class="tm-session-usage" :title="`Session totals across ${chatTotals.turns} turn${chatTotals.turns === 1 ? '' : 's'}`">
            <span class="tm-usage-label">Session</span>
            <span class="tm-usage-pair">
              <span title="Input tokens (uncached)">↑ {{ formatNumber(chatTotals.input) }}</span>
              <span title="Output tokens">↓ {{ formatNumber(chatTotals.output) }}</span>
              <span v-if="chatTotals.cacheRead" class="tm-usage-cache" title="Prompt tokens served from cache">⚡ {{ formatNumber(chatTotals.cacheRead) }}</span>
              <span v-if="chatTotals.toolCalls" class="tm-usage-tools" title="Tool calls">⚙ {{ chatTotals.toolCalls }}</span>
              <span class="tm-usage-total">= {{ formatNumber(chatTotals.total) }}</span>
              <span v-if="chatTotals.latencyMs" class="tm-usage-latency" title="Total time across the session">⏱ {{ formatLatency(chatTotals.latencyMs) }}</span>
            </span>
            <span class="tm-session-cost" :title="chatTotals.reported ? 'Includes provider-reported costs' : `Estimated cost (${pricingLabel()} rates)`">≈ {{ formatCost(chatTotals.cost) }}</span>
          </div>
          <button class="tm-btn-ghost" type="button" @click="resetChat"
            :disabled="activeChat.history.length === 0 && !inflight">
            <font-awesome-icon :icon="['fas', 'trash']" /> Clear
          </button>
        </div>

        <div class="tm-chat-list" ref="chatList">
          <div v-if="activeChat.history.length === 0 && !inflight" class="tm-empty">
            <font-awesome-icon class="tm-empty-icon" :icon="['fas', 'wand-sparkles']" />
            <p>{{ activeTab === 'chat' ? 'Chat with Teammate — it can act on your workspace.' : 'Start a conversation with Teammate.' }}</p>
            <p class="tm-empty-hint">POST <code>/api/teammate/{{ activeTab }}</code></p>
          </div>

          <div v-for="(msg, i) in activeChat.history" :key="i" :class="['tm-msg', `tm-msg--${msg.role}`]">
            <div class="tm-msg-avatar">
              <font-awesome-icon v-if="msg.role === 'user'" :icon="['fas', 'user']" />
              <font-awesome-icon v-else :icon="['fas', 'wand-magic-sparkles']" />
            </div>
            <div class="tm-msg-body">
              <div class="tm-msg-role">{{ msg.role }}</div>
              <div class="tm-msg-content tm-md" v-html="renderMarkdown(msg.content)"></div>
              <div v-if="msg.attachments && msg.attachments.length" class="tm-msg-attachments">
                <span v-for="(att, ai) in msg.attachments" :key="ai" class="tm-msg-att">
                  <img v-if="att.isImage" :src="att.dataUrl" class="tm-msg-att-thumb" :alt="att.name" :title="att.name" />
                  <span v-else class="tm-msg-att-file" :title="att.name">
                    <font-awesome-icon :icon="['far', 'file']" /> {{ att.name }}
                  </span>
                </span>
              </div>

              <!-- /chat: activities run + modules committed this turn -->
              <div v-if="msg.actions && msg.actions.length" class="tm-msg-extra tm-actions">
                <div class="tm-extra-label">Actions performed</div>
                <div class="tm-action-list">
                  <template v-for="(act, ai) in msg.actions">
                    <!-- successful submission (form entry or module) → card -->
                    <div v-if="!act.error" :key="`card-${ai}`"
                      class="tm-sub-card" :class="`tm-sub-card--${act.type || 'activity'}`">
                      <div class="tm-sub-head">
                        <span class="tm-sub-icon">
                          <font-awesome-icon :icon="act.type === 'module' ? ['fas', 'project-diagram'] : ['far', 'file']" />
                        </span>
                        <div class="tm-sub-headtext">
                          <div class="tm-sub-title">
                            <span class="tm-sub-verb">{{ actionLabel(act) }}</span>
                            <span v-if="act.module" class="tm-sub-module">{{ act.module }}</span>
                          </div>
                          <div v-if="actionSubId(act)" class="tm-sub-subid">{{ actionSubId(act) }}</div>
                        </div>
                        <span v-if="act.state" class="tm-sub-state">{{ act.state }}</span>
                        <span v-if="act.flagged" class="tm-sub-flag" title="Flagged for review">⚑</span>
                      </div>
                      <dl v-if="actionFields(act).length" class="tm-sub-fields">
                        <template v-for="(f, fi) in actionFields(act)">
                          <dt :key="`dt-${ai}-${fi}`">{{ f.label }}</dt>
                          <dd :key="`dd-${ai}-${fi}`">{{ f.value }}</dd>
                        </template>
                      </dl>
                      <div v-if="actionHref(act) || availableActions(act).length" class="tm-sub-foot">
                        <button v-for="(a, aci) in availableActions(act)" :key="`act-${ai}-${aci}`"
                          type="button" class="tm-sub-action"
                          :class="{ 'tm-sub-action--primary': a.primary }"
                          :disabled="inflight" @click="triggerAction(act, a)">
                          {{ a.label }}
                        </button>
                        <a v-if="actionHref(act)" :href="actionHref(act)" class="tm-sub-link">
                          {{ actionHrefLabel(act) }}
                          <font-awesome-icon :icon="['fas', 'up-right-from-square']" />
                        </a>
                      </div>
                    </div>
                    <!-- failed action → compact error row -->
                    <div v-else :key="`err-${ai}`" class="tm-action-item is-error">
                      <font-awesome-icon class="tm-action-ic" :icon="['fas', 'exclamation-circle']" />
                      <span class="tm-action-activity">{{ actionLabel(act) }}</span>
                      <span class="tm-action-meta">
                        <template v-if="act.module">{{ act.module }}</template>
                        <template v-if="act.entryId"> · entry {{ act.entryId }}</template>
                      </span>
                      <span class="tm-action-err">{{ act.message || act.error }}</span>
                    </div>
                  </template>
                </div>
              </div>

              <!-- /chat: human / hybrid activities awaiting the user's confirmation -->
              <div v-if="msg.pendingActions && msg.pendingActions.length" class="tm-msg-extra tm-pending">
                <div class="tm-extra-label">Awaiting your confirmation</div>
                <div v-for="(pa, pai) in msg.pendingActions" :key="pai" class="tm-pending-item">
                  <div class="tm-pending-info">
                    <span v-if="pa.actor" :class="['tm-pending-actor', `tm-pending-actor--${pa.actor}`]">{{ pa.actor }}</span>
                    <span class="tm-action-activity">{{ isAccessCard(pa) ? 'Access change' : (pa.activity || 'activity') }}</span>
                    <span class="tm-action-meta">
                      <template v-if="pa.module">{{ pa.module }}</template>
                      <template v-if="pa.entryId"> · entry {{ pa.entryId }}</template>
                      <template v-if="pa.state"> · {{ pa.state }}</template>
                    </span>
                    <!-- An access card (§13.7) lists its changes and warnings before Confirm. -->
                    <ul v-if="isAccessCard(pa)" class="tm-access-changes">
                      <li v-for="(row, ri) in accessChangeRows(pa)" :key="`acr-${ri}`">
                        {{ row.label }}: {{ row.before }} → {{ row.after }}<template v-if="row.widens"> (lets more people in)</template>
                      </li>
                      <li v-for="(w, wi) in (pa.warnings || [])" :key="`acw-${wi}`" class="tm-access-warning">{{ w.message }}</li>
                    </ul>
                  </div>
                  <button class="tm-confirm-btn" type="button"
                    :disabled="inflight || pa._status === 'committing' || pa._status === 'done'"
                    @click="confirmItem(pa, 'activity')">
                    <font-awesome-icon v-if="pa._status === 'committing'" :icon="['fas', 'spinner']" spin />
                    <font-awesome-icon v-else-if="pa._status === 'done'" :icon="['fas', 'check']" />
                    {{ pa._status === 'done' ? 'Confirmed' : 'Confirm' }}
                  </button>
                </div>
              </div>

              <!-- /chat: human / no-actor activities for the user to complete — open the form -->
              <div v-if="msg.formPills && msg.formPills.length" class="tm-msg-extra tm-formpills">
                <div class="tm-extra-label">Open to complete</div>
                <div class="tm-formpill-list">
                  <button v-for="(pa, pi) in msg.formPills" :key="`fp-${pi}`" type="button"
                    class="tm-formpill" @click="openFormPill(pa)">
                    <font-awesome-icon class="tm-formpill-ic" :icon="['far', 'file']" />
                    <span class="tm-formpill-activity">{{ formPillLabel(pa) }}</span>
                    <span class="tm-action-meta">
                      <template v-if="pa.module">{{ pa.module }}</template>
                      <template v-if="pa.entryId"> · entry {{ pa.entryId }}</template>
                    </span>
                    <font-awesome-icon :icon="['fas', 'up-right-from-square']" />
                  </button>
                </div>
              </div>

              <!-- /chat: design / modify proposals — reviewed, then committed via button -->
              <div v-if="msg.proposals && msg.proposals.length" class="tm-proposals">
                <div v-for="(p, pi) in msg.proposals" :key="pi" class="tm-proposal">
                  <div class="tm-proposal-head">
                    <font-awesome-icon :icon="['fas', 'project-diagram']" />
                    <span class="tm-proposal-name">{{ proposalName(p) }}</span>
                    <span v-if="p.kind" class="tm-proposal-phase">{{ p.kind }}</span>
                    <span :class="['tm-proposal-valid', proposalValid(p) ? 'is-valid' : 'is-invalid']">
                      {{ proposalValid(p) ? 'valid' : 'invalid' }}
                    </span>
                  </div>
                  <div v-if="p.message" class="tm-proposal-msg">{{ p.message }}</div>
                  <div class="tm-proposal-counts">
                    <span>{{ proposalCount(p, 'information') }} fields</span>
                    <span>{{ proposalCount(p, 'states') }} states</span>
                    <span>{{ proposalCount(p, 'activities') }} activities</span>
                    <span>{{ proposalCount(p, 'flows') }} flows</span>
                  </div>
                  <div v-if="proposalErrors(p)" class="tm-proposal-errors">{{ proposalErrors(p) }}</div>
                  <details class="tm-proposal-schema">
                    <summary>View schema JSON</summary>
                    <pre>{{ prettyJson(proposalSchema(p)) }}</pre>
                  </details>
                  <div class="tm-proposal-actions">
                    <button class="tm-confirm-btn" type="button"
                      :disabled="inflight || !proposalValid(p) || p._status === 'committing' || p._status === 'done'"
                      @click="confirmItem(p, 'module')">
                      <font-awesome-icon v-if="p._status === 'committing'" :icon="['fas', 'spinner']" spin />
                      <font-awesome-icon v-else-if="p._status === 'done'" :icon="['fas', 'check']" />
                      {{ proposalCommitLabel(p) }}
                    </button>
                    <span v-if="!proposalValid(p)" class="tm-proposal-hint">Fix validation errors before applying.</span>
                    <!-- Once committed, a clickable chip links to the created/updated module — the
                         receipt, in place of a separate "Created module" card. -->
                    <span v-if="p._status === 'done' && proposalModuleChip(p)" class="tm-proposal-modulechip"
                      v-html="proposalModuleChip(p)"></span>
                  </div>
                </div>
              </div>

              <!-- /chat: next ai_instruction surfaced by an activity -->
              <div v-if="msg.aiInstruction" class="tm-msg-extra">
                <div class="tm-extra-label">ai_instruction</div>
                <pre>{{ msg.aiInstruction }}</pre>
              </div>

              <!-- /converse: proposed action (read-only suggestion) -->
              <div v-if="msg.proposedAction" class="tm-msg-extra">
                <div class="tm-extra-label">Proposed action</div>
                <pre>{{ prettyJson(msg.proposedAction) }}</pre>
              </div>
              <div v-if="msg.suggestedFollowups && msg.suggestedFollowups.length"
                class="tm-followups">
                <button v-for="(s, j) in msg.suggestedFollowups" :key="j" type="button"
                  class="tm-followup" @click="activeChat.draft = String(s)">
                  {{ s }}
                </button>
              </div>
              <div v-if="msg.usage && (msg.usage.input || msg.usage.output)" class="tm-msg-usage">
                <span v-if="msg.usage.model" class="tm-usage-model" :title="`Model: ${msg.usage.model}`">🧠 {{ formatModel(msg.usage.model) }}</span>
                <span title="Input tokens (uncached)">↑ {{ formatNumber(msg.usage.input) }}</span>
                <span title="Output tokens">↓ {{ formatNumber(msg.usage.output) }}</span>
                <span v-if="msg.usage.cacheRead" class="tm-usage-cache" title="Prompt tokens served from cache (~0.1x price)">⚡ {{ formatNumber(msg.usage.cacheRead) }} cached</span>
                <span v-if="msg.usage.cacheWrite" class="tm-usage-cache" title="Prompt tokens written to cache (~1.25x price)">✎ {{ formatNumber(msg.usage.cacheWrite) }} cache write</span>
                <span v-if="msg.usage.toolCalls" class="tm-usage-tools" title="Tool calls this turn">⚙ {{ msg.usage.toolCalls }} tool {{ msg.usage.toolCalls === 1 ? 'call' : 'calls' }}</span>
                <span class="tm-usage-total" title="Total tokens incl. cached">{{ formatNumber(totalTokens(msg.usage)) }} tokens</span>
                <span v-if="msg.usage.latencyMs" class="tm-usage-latency" title="Time taken this turn">⏱ {{ formatLatency(msg.usage.latencyMs) }}</span>
                <span class="tm-msg-cost" :title="costTitle(msg.usage)">{{ msg.usage.cost > 0 ? '' : '≈ ' }}{{ formatCost(costOf(msg.usage)) }}</span>
              </div>
              <div v-if="msg.error" class="tm-msg-error">{{ msg.error }}</div>
            </div>
          </div>

          <div v-if="inflight && (currentAction === 'converse' || currentAction === 'chat')" class="tm-msg tm-msg--assistant">
            <div class="tm-msg-avatar">
              <font-awesome-icon :icon="['fas', 'spinner']" spin />
            </div>
            <div class="tm-msg-body">
              <div class="tm-msg-role">assistant</div>
              <div class="tm-msg-content tm-msg-loading">{{ progressLabel || "Thinking…" }}</div>
            </div>
          </div>
        </div>

        <!-- Attachments fold into the composer: chips sit above the input, the "+" opens the
             picker, and paste (textarea) / drag-drop (whole input) add files too. -->
        <div class="tm-chat-input" :class="{ 'tm-chat-input--drag': chatAttachDragOver }"
          @dragover.prevent="onChatDragOver" @dragenter.prevent="onChatDragOver"
          @dragleave="onChatDragLeave" @drop.prevent="onChatDrop">
          <teammate-attachments ref="chatAttach" compact v-model="activeChat.attachments" :disabled="inflight"
            :ws-id="selectedWorkspaceId" :session-id="activeChat.sessionId" />
          <div class="tm-chat-input-row">
            <button class="tm-btn-attach" type="button" :disabled="inflight"
              title="Attach images &amp; files" @click="pickChatAttachment">
              <font-awesome-icon :icon="['fas', 'plus']" />
            </button>
            <textarea ref="composer" rows="1" v-model="activeChat.draft" :disabled="inflight"
              placeholder="Message Teammate. Shift+Enter for newline."
              @keydown="onComposerKeydown" @input="autoGrow" @paste="onComposerPaste" />
            <button class="tm-btn-send" type="button" :disabled="!canSend"
              @click="sendChat" title="Send">
              <font-awesome-icon :icon="['far', 'paper-plane']" />
            </button>
          </div>
        </div>
      </section>

      <!-- ============== EXECUTE ============== -->
      <section v-if="activeTab === 'execute'" class="tm-pane tm-pane--form">
        <div v-if="!selectedModuleName" class="tm-hint">
          <font-awesome-icon :icon="['fas', 'circle-info']" /> Select a module above to enable Execute.
        </div>
        <div class="tm-form">
          <div class="tm-field">
            <label>Entry ID</label>
            <input type="text" v-model.trim="execute.entryId" placeholder="(optional)" />
          </div>
          <div class="tm-field tm-field-full">
            <label>Instruction <span class="tm-req">*</span></label>
            <textarea v-model="execute.instruction" rows="3"
              placeholder="What should Teammate do?" />
          </div>
          <div class="tm-field tm-field-full">
            <label>Context (JSON, optional)</label>
            <textarea v-model="execute.contextStr" rows="3" class="tm-mono"
              placeholder='{ "key": "value" }' />
            <span v-if="execute.contextError" class="tm-field-error">{{ execute.contextError }}</span>
          </div>
          <div class="tm-field tm-field-full">
            <label>Attachments</label>
            <teammate-attachments v-model="execute.attachments" :disabled="inflight" :ws-id="selectedWorkspaceId" />
          </div>
        </div>

        <div class="tm-form-actions">
          <button class="tm-btn-primary" type="button" :disabled="!canSendExecute || inflight"
            @click="sendExecute">
            <font-awesome-icon v-if="inflight && currentAction === 'execute'" :icon="['fas', 'spinner']" spin />
            <font-awesome-icon v-else :icon="['far', 'paper-plane']" />
            Send
          </button>
        </div>
      </section>

      <!-- ============== CHOREOGRAPH ============== -->
      <section v-if="activeTab === 'choreograph'" class="tm-pane tm-pane--form">
        <div v-if="!selectedModuleName" class="tm-hint">
          <font-awesome-icon :icon="['fas', 'circle-info']" /> Select a module above to enable Choreograph.
        </div>
        <div class="tm-form">
          <div class="tm-field">
            <label>Entry ID <span class="tm-req">*</span></label>
            <input type="text" v-model.trim="choreograph.entryId" />
          </div>
          <div class="tm-field">
            <label>Trigger</label>
            <input type="text" v-model.trim="choreograph.trigger" placeholder="user_initiated" />
          </div>
          <div class="tm-field">
            <label>Max Depth</label>
            <input type="number" v-model.number="choreograph.maxDepth" min="1" max="50" />
          </div>
          <div class="tm-field tm-field-full">
            <label>ai_instruction <span class="tm-req">*</span></label>
            <textarea v-model="choreograph.aiInstruction" rows="3" />
          </div>
          <div class="tm-field tm-field-full">
            <label>Attachments</label>
            <teammate-attachments v-model="choreograph.attachments" :disabled="inflight" :ws-id="selectedWorkspaceId" />
          </div>
        </div>

        <div class="tm-form-actions">
          <button class="tm-btn-primary" type="button"
            :disabled="!canSendChoreograph || inflight"
            @click="sendChoreograph">
            <font-awesome-icon v-if="inflight && currentAction === 'choreograph'" :icon="['fas', 'spinner']" spin />
            <font-awesome-icon v-else :icon="['far', 'paper-plane']" />
            Start
          </button>
        </div>

        <div v-if="choreograph.runId" class="tm-runner">
          <div class="tm-runner-row">
            <div>
              <span class="tm-runner-label">Run ID</span>
              <code>{{ choreograph.runId }}</code>
            </div>
            <div>
              <span class="tm-runner-label">Status</span>
              <span :class="['tm-status', `tm-status--${(choreograph.runStatus || '').toLowerCase()}`]">
                {{ choreograph.runStatus || '—' }}
              </span>
            </div>
            <div class="tm-runner-actions">
              <label class="tm-poll-toggle">
                <input type="checkbox" v-model="choreograph.autoPoll"
                  @change="toggleAutoPoll" />
                Auto poll
              </label>
              <button class="tm-btn-ghost" type="button" @click="pollChoreography(choreograph.runId)"
                :disabled="inflight">
                <font-awesome-icon :icon="['fas', 'sync-alt']" /> Refresh
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- ============== DESIGN ============== -->
      <section v-if="activeTab === 'design'" class="tm-pane tm-pane--form">
        <div class="tm-form">
          <div class="tm-field tm-field-full">
            <label>Description <span class="tm-req">*</span></label>
            <textarea v-model="design.description" rows="4"
              placeholder="Describe the module you want Teammate to design." />
          </div>
          <div class="tm-field tm-field-full">
            <label>Context (JSON, optional)</label>
            <textarea v-model="design.contextStr" rows="3" class="tm-mono"
              placeholder='{ "key": "value" }' />
            <span v-if="design.contextError" class="tm-field-error">{{ design.contextError }}</span>
          </div>
          <div class="tm-field tm-field-full">
            <label class="tm-checkbox">
              <input type="checkbox" v-model="design.autoCreate" />
              Auto-create the module on success
            </label>
          </div>
          <div class="tm-field tm-field-full">
            <label>Attachments</label>
            <teammate-attachments v-model="design.attachments" :disabled="inflight" :ws-id="selectedWorkspaceId" />
          </div>
        </div>

        <div class="tm-form-actions">
          <button class="tm-btn-primary" type="button"
            :disabled="!canSendDesign || inflight" @click="sendDesign">
            <font-awesome-icon v-if="inflight && currentAction === 'design'" :icon="['fas', 'spinner']" spin />
            <font-awesome-icon v-else :icon="['far', 'paper-plane']" />
            Design
          </button>
        </div>
      </section>

      <!-- ============== MODIFY ============== -->
      <section v-if="activeTab === 'modify'" class="tm-pane tm-pane--form">
        <div v-if="!selectedModuleName" class="tm-hint">
          <font-awesome-icon :icon="['fas', 'circle-info']" /> Select a module above to enable Modify.
        </div>
        <div class="tm-form">
          <div class="tm-field">
            <label class="tm-checkbox">
              <input type="checkbox" v-model="modify.autoApply" />
              Auto-apply changes
            </label>
          </div>
          <div class="tm-field tm-field-full">
            <label>Instruction <span class="tm-req">*</span></label>
            <textarea v-model="modify.instruction" rows="4" />
          </div>
          <div class="tm-field tm-field-full">
            <label>Attachments</label>
            <teammate-attachments v-model="modify.attachments" :disabled="inflight" :ws-id="selectedWorkspaceId" />
          </div>
        </div>

        <div class="tm-form-actions">
          <button class="tm-btn-primary" type="button"
            :disabled="!canSendModify || inflight" @click="sendModify">
            <font-awesome-icon v-if="inflight && currentAction === 'modify'" :icon="['fas', 'spinner']" spin />
            <font-awesome-icon v-else :icon="['far', 'paper-plane']" />
            Modify
          </button>
        </div>
      </section>

      <!-- ============== Response viewer (shared) ============== -->
      <aside v-if="!isChatTab" class="tm-response">
        <div class="tm-response-header">
          <span class="tm-response-title">Response</span>
          <span v-if="response.status" :class="['tm-pill', response.ok ? 'tm-pill--ok' : 'tm-pill--err']">
            HTTP {{ response.status }}
          </span>
          <span v-if="response.usage && response.usage.model" class="tm-pill tm-pill--usage" :title="`Model: ${response.usage.model}`">
            🧠 {{ formatModel(response.usage.model) }}
          </span>
          <span v-if="response.usage" class="tm-pill tm-pill--usage" title="Token usage">
            ↑ {{ formatNumber(response.usage.input) }}
            · ↓ {{ formatNumber(response.usage.output) }}
            <template v-if="response.usage.cacheRead"> · ⚡ {{ formatNumber(response.usage.cacheRead) }} cached</template>
            <template v-if="response.usage.toolCalls"> · ⚙ {{ response.usage.toolCalls }}</template>
            · {{ formatNumber(totalTokens(response.usage)) }} total
            <template v-if="response.usage.latencyMs"> · ⏱ {{ formatLatency(response.usage.latencyMs) }}</template>
            <template v-if="response.usage.cost > 0"> · {{ formatCost(response.usage.cost) }}</template>
          </span>
          <button v-if="response.body" class="tm-btn-ghost tm-btn-xs" type="button"
            @click="copyResponse">
            <font-awesome-icon :icon="['far', 'clipboard']" /> Copy
          </button>
        </div>
        <div v-if="response.message" class="tm-response-message">{{ response.message }}</div>
        <pre v-if="response.body" class="tm-response-body">{{ response.body }}</pre>
        <div v-else-if="!inflight" class="tm-response-empty">No response yet.</div>
      </aside>
    </main>
  </div>
</template>

<script>
import { commonStore } from "../store/commonStore";
import LocalStorageService from "../store/LocalStorageService";
import webAppConfig from "../commons/webApp.config";
import commons from "../commons/commons";
import TeammateAttachments from "./TeammateAttachments";
import teammateChatMixin from "./teammateChatMixin";
import signalr from "../store/signalr";
import { isAccessCard, accessChangeRows } from "./accessCard";

const TABS = [
  { id: "chat", label: "Chat" },
  { id: "converse", label: "Converse" },
  { id: "execute", label: "Execute" },
  { id: "choreograph", label: "Choreograph" },
  { id: "design", label: "Design" },
  { id: "modify", label: "Modify" },
];

export default {
  name: "FTSTeammate",
  mixins: [teammateChatMixin],
  components: {
    "teammate-attachments": TeammateAttachments,
  },
  data() {
    return {
      tabs: TABS,
      activeTab: "chat",
      inflight: false,
      currentAction: null,
      // true while a file is dragged over the chat input, to show the drop outline
      chatAttachDragOver: false,
      // Transient label of the step the teammate is currently running this turn, pushed over
      // SignalR (signalr.teammateProgress). null between turns; shown in the "Thinking…" bubble.
      progressLabel: null,

      workspaces: [],
      loadingWorkspaces: false,
      selectedWorkspaceId: "",
      selectedWorkspaceLabel: "",
      wsOpen: false,
      wsSearch: "",
      wsSearching: false,
      wsActiveIndex: 0,
      modules: [],
      loadingModules: false,
      selectedModuleName: "",

      // /api/teammate/meta — active provider info. When the server runs on
      // OpenRouter (modelSelectable), the user may pick a model per request.
      meta: { provider: "", defaultModel: "", modelSelectable: false },
      modelOverride: "",
      modelOptions: [],

      chat: {
        entryId: "",
        draft: "",
        history: [],
        attachments: [],
        // Stable per-conversation id sent as SessionId on every /chat turn (message and
        // confirm-button), so the server groups this session's trace into one log file.
        // resetChat() mints a fresh one when a new conversation starts.
        sessionId: commons.newUUID(),
        // Opt-in to the design/modify tools. The server only honours this for
        // Administrator / Workspace Admin accounts; otherwise chat stays read/act only.
        design: false,
      },

      converse: {
        entryId: "",
        draft: "",
        history: [],
        attachments: [],
      },

      execute: {
        entryId: "",
        instruction: "",
        contextStr: "",
        contextError: "",
        attachments: [],
      },

      choreograph: {
        entryId: "",
        trigger: "user_initiated",
        aiInstruction: "",
        maxDepth: 10,
        runId: "",
        runStatus: "",
        autoPoll: false,
        pollTimer: null,
        attachments: [],
      },

      design: {
        description: "",
        contextStr: "",
        contextError: "",
        autoCreate: false,
        attachments: [],
      },

      modify: {
        instruction: "",
        autoApply: false,
        attachments: [],
      },

      response: {
        status: null,
        ok: false,
        body: "",
        message: "",
        usage: null,
      },
    };
  },
  computed: {
    selectedWorkspaceName() {
      const ws = this.workspaces.find((w) => String(w.id) === String(this.selectedWorkspaceId));
      if (ws) return ws.name || ws.id;
      // results may not contain the selected workspace (server-filtered), so fall back
      return this.selectedWorkspaceLabel || "";
    },
    // server already filters by search term; expose the current results to the template
    filteredWorkspaces() {
      return this.workspaces;
    },
    // Both the Chat and Converse tabs share one chat UI; this resolves to the
    // conversation state for whichever is active (falls back to converse).
    activeChat() {
      return this.activeTab === "chat" ? this.chat : this.converse;
    },
    isChatTab() {
      return this.activeTab === "chat" || this.activeTab === "converse";
    },
    canSend() {
      const c = this.activeChat;
      return !this.inflight && (c.draft.trim().length > 0 || c.attachments.length > 0);
    },
    canSendExecute() {
      return !!this.selectedModuleName && this.execute.instruction.trim().length > 0;
    },
    canSendChoreograph() {
      const c = this.choreograph;
      return !!this.selectedModuleName && c.entryId.trim().length > 0 && c.aiInstruction.trim().length > 0;
    },
    canSendDesign() {
      return this.design.description.trim().length > 0;
    },
    canSendModify() {
      return !!this.selectedModuleName && this.modify.instruction.trim().length > 0;
    },
    chatTotals() {
      return this.computeTotals(this.activeChat.history);
    },
  },
  methods: {
    /** Module source for shared reference/link resolution (mixin hook). */
    chatModules() {
      return this.modules;
    },
    isAccessCard(pa) {
      return isAccessCard(pa);
    },
    accessChangeRows(pa) {
      return accessChangeRows(pa);
    },
    onComposerPaste(evt) {
      const items = evt.clipboardData && evt.clipboardData.items;
      const ref = this.$refs.chatAttach;
      if (!items || !ref || typeof ref.addClipboardItems !== "function") return;
      // only consume the event when actual files (e.g. a pasted screenshot) are present
      if (ref.addClipboardItems(items)) evt.preventDefault();
    },
    /** "+" button → open the OS file picker on the (folded-in) attachments component. */
    pickChatAttachment() {
      const ref = this.$refs.chatAttach;
      if (ref && typeof ref.pickFiles === "function") ref.pickFiles();
    },
    onChatDragOver() {
      if (!this.inflight) this.chatAttachDragOver = true;
    },
    onChatDragLeave(evt) {
      // ignore moves between children; only clear when the cursor truly leaves the input
      if (evt.currentTarget.contains(evt.relatedTarget)) return;
      this.chatAttachDragOver = false;
    },
    onChatDrop(evt) {
      this.chatAttachDragOver = false;
      if (this.inflight) return;
      const dt = evt.dataTransfer;
      const ref = this.$refs.chatAttach;
      if (dt && dt.files && dt.files.length && ref && typeof ref.addFiles === "function") {
        ref.addFiles(dt.files);
      }
    },
    requestConfig() {
      const wsId = this.selectedWorkspaceId;
      return wsId ? { headers: { wsId } } : {};
    },
    /** teammateChatMixin hook: workspace id for chat-attachment download URLs. */
    chatWorkspaceId() {
      return this.selectedWorkspaceId || "";
    },
    initialWorkspaceId() {
      try {
        if (this.$cookies && typeof this.$cookies.get === "function") {
          const v = this.$cookies.get("wsId");
          if (v) return v;
        }
      } catch (e) { /* ignore */ }
      try {
        return LocalStorageService.getWorkspaceId(webAppConfig.baseServer) || "";
      } catch (e) {
        return "";
      }
    },
    async loadMeta() {
      try {
        const res = await commonStore.state.client.get("/api/teammate/meta");
        const m = res.data || {};
        this.meta = {
          provider: m.provider || "",
          defaultModel: m.defaultModel || "",
          modelSelectable: !!m.modelSelectable,
        };
        if (this.meta.modelSelectable) {
          try { this.modelOverride = window.localStorage.getItem("tmModelOverride") || ""; } catch (e) { /* ignore */ }
          this.loadModelOptions();
        }
      } catch (err) {
        // best-effort: without meta the model picker simply stays hidden
        console.error("[Teammate] loadMeta", err);
      }
    },
    /**
     * Populate the model datalist from the active provider's catalog. Proxied through the
     * backend (/api/teammate/models) rather than hitting openrouter.ai directly — the app's
     * Content-Security-Policy blocks that cross-origin call.
     */
    async loadModelOptions() {
      try {
        const res = await commonStore.state.client.get("/api/teammate/models");
        const ids = (res.data && Array.isArray(res.data.models)) ? res.data.models : [];
        if (ids.length) this.modelOptions = ids;
      } catch (err) {
        // offline / blocked — the input still accepts a free-typed model id
      }
    },
    /** Attach the model override (OpenRouter only) to a request payload. */
    applyModelOverride(payload) {
      if (this.meta.modelSelectable && this.modelOverride) payload.Model = this.modelOverride;
      return payload;
    },
    async loadWorkspaces() {
      this.loadingWorkspaces = true;
      try {
        const res = await commonStore.state.client.get("/api/workspace?search=");
        this.workspaces = Array.isArray(res.data) ? res.data : [];
      } catch (err) {
        console.error("[Teammate] loadWorkspaces", err);
        this.workspaces = [];
      } finally {
        this.loadingWorkspaces = false;
      }
    },
    async loadModules(wsId) {
      if (!wsId) { this.modules = []; return; }
      this.loadingModules = true;
      try {
        const res = await commonStore.state.client.get(`/api/workspace/${wsId}`, { headers: { wsId } });
        const ws = res.data || {};
        // modules live in `vectors` (the app's workspaceModules); raw `menus`
        // only reference them by id until commons.expandMenu() links them.
        const vectors = Array.isArray(ws.vectors) ? ws.vectors : [];
        const seen = new Set();
        this.modules = vectors.filter((m) => {
          if (!m || !m.name) return false;
          if (seen.has(m.name)) return false;
          seen.add(m.name);
          return true;
        });
      } catch (err) {
        console.error("[Teammate] loadModules", err);
        this.modules = [];
      } finally {
        this.loadingModules = false;
      }
    },
    isWsSelected(ws) {
      return String(ws.id) === String(this.selectedWorkspaceId);
    },
    async searchWorkspaces(term) {
      const seq = ++this.wsSearchSeq;
      this.wsSearching = true;
      try {
        const q = encodeURIComponent(term || "");
        const res = await commonStore.state.client.get(`/api/workspace?search=${q}`);
        if (seq !== this.wsSearchSeq) return; // a newer search superseded this one
        this.workspaces = Array.isArray(res.data) ? res.data : [];
        const selectedIdx = this.workspaces.findIndex((w) => this.isWsSelected(w));
        this.wsActiveIndex = selectedIdx >= 0 ? selectedIdx : 0;
      } catch (err) {
        if (seq !== this.wsSearchSeq) return;
        console.error("[Teammate] searchWorkspaces", err);
        this.workspaces = [];
        this.wsActiveIndex = 0;
      } finally {
        if (seq === this.wsSearchSeq) this.wsSearching = false;
      }
    },
    openWsDropdown() {
      if (this.loadingWorkspaces) return;
      this.wsSearch = "";
      this.wsOpen = true;
      // refresh with the unfiltered list so a prior search doesn't linger
      this.searchWorkspaces("");
    },
    closeWsDropdown() {
      this.wsOpen = false;
      this.wsSearch = "";
    },
    toggleWsDropdown() {
      if (this.wsOpen) {
        this.closeWsDropdown();
      } else {
        if (this.$refs.wsInput) this.$refs.wsInput.focus();
        this.openWsDropdown();
      }
    },
    onWsSearchInput(evt) {
      this.wsSearch = evt.target.value;
      this.wsOpen = true;
      this.debouncedWsSearch(this.wsSearch);
    },
    selectWorkspace(ws) {
      this.selectedWorkspaceId = ws.id;
      this.selectedWorkspaceLabel = ws.name || String(ws.id);
      this.closeWsDropdown();
      if (this.$refs.wsInput) this.$refs.wsInput.blur();
    },
    onWsKeydown(evt) {
      switch (evt.key) {
        case "ArrowDown":
          evt.preventDefault();
          if (!this.wsOpen) { this.openWsDropdown(); break; }
          this.wsActiveIndex = Math.min(this.wsActiveIndex + 1, this.filteredWorkspaces.length - 1);
          break;
        case "ArrowUp":
          evt.preventDefault();
          this.wsActiveIndex = Math.max(this.wsActiveIndex - 1, 0);
          break;
        case "Enter": {
          if (!this.wsOpen) break;
          evt.preventDefault();
          const ws = this.filteredWorkspaces[this.wsActiveIndex];
          if (ws) this.selectWorkspace(ws);
          break;
        }
        case "Escape":
          if (!this.wsOpen) break;
          evt.preventDefault();
          this.closeWsDropdown();
          if (this.$refs.wsInput) this.$refs.wsInput.blur();
          break;
        default:
          break;
      }
    },
    onDocumentClick(evt) {
      if (this.wsOpen && this.$refs.wsCombo && !this.$refs.wsCombo.contains(evt.target)) {
        this.closeWsDropdown();
      }
    },
    parseJson(str, fieldKey) {
      const trimmed = (str || "").trim();
      if (!trimmed) return null;
      try {
        return JSON.parse(trimmed);
      } catch (e) {
        if (fieldKey) this[fieldKey] = "Invalid JSON: " + e.message;
        throw e;
      }
    },
    setResponse({ status, ok, data, message }) {
      this.response.status = status != null ? status : null;
      this.response.ok = !!ok;
      this.response.body = data !== undefined ? this.prettyJson(data) : "";
      this.response.message = message || "";
      this.response.usage = ok ? this.extractUsage(data) : null;
    },
    handleError(err, action) {
      const status = err && err.response && err.response.status;
      const data = err && err.response && err.response.data;
      const fallbackMessage = (err && err.message) || "Request failed";
      const message = (data && (data.message || data.error)) || fallbackMessage;
      this.setResponse({ status, ok: false, data: data !== undefined ? data : fallbackMessage, message });
      if (this.$toasted && this.$toasted.global && this.$toasted.global.error) {
        this.$toasted.global.error(err, message);
      }
      console.error(`[Teammate:${action}]`, err);
    },
    onComposerKeydown(evt) {
      // A CJK IME commit fires keydown with key === "Enter" — don't send half-composed text.
      // keyCode 229 covers older Safari, which doesn't set isComposing on that event.
      if (evt.isComposing || evt.keyCode === 229) return;
      if (evt.key === "Enter" && !evt.shiftKey) {
        evt.preventDefault();
        if (this.canSend) this.sendChat();
      }
    },
    autoGrow() {
      const el = this.$refs.composer;
      if (!el) return;
      el.style.height = "auto";
      el.style.height = Math.min(el.scrollHeight, 180) + "px";
    },
    scrollChatToEnd() {
      this.$nextTick(() => {
        const el = this.$refs.chatList;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    resetChat() {
      this.activeChat.history = [];
      // A cleared chat is a new conversation — give it a fresh trace session id, and move the
      // SignalR progress subscription to it so live progress follows the new conversation.
      if (this.activeTab === "chat") {
        const prev = this.chat.sessionId;
        this.chat.sessionId = commons.newUUID();
        if (prev) signalr.leaveSession(prev);
        signalr.joinSession(this.chat.sessionId);
      }
      this.progressLabel = null;
    },

    /**
     * Live progress frame from the teammate's current turn (SignalR). Ignores frames for other
     * chat sessions, and is a no-op once the turn is no longer inflight (the real response, which
     * arrives in the /chat body, is authoritative — a late/dropped frame must not linger).
     */
    onTeammateProgress(p) {
      if (!p || p.sessionId !== this.chat.sessionId) return;
      if (!this.inflight) return;
      // Only advance the label when a new tool starts; leave the last action showing through
      // tool_done so it stays readable (tools finish in ms — clearing on done just flickers).
      // The turn's start and finally blocks reset it.
      if (p.phase === "tool") {
        const label = commons.describeTeammateProgress(p);
        if (label) this.progressLabel = label;
      }
    },

    /** Maps a /chat (or /converse) response body into a chat-bubble message. */
    buildAssistantMessage(data, isChat) {
      const msg = {
        role: "assistant",
        content: data.response || "",
        usage: this.extractUsage(data),
        error: null,
      };
      if (isChat) {
        msg.actions = Array.isArray(data.actions) ? data.actions : [];
        msg.proposals = Array.isArray(data.proposals) ? data.proposals : [];
        msg.pendingActions = Array.isArray(data.pendingActions) ? data.pendingActions : [];
        msg.formPills = Array.isArray(data.formPills) ? data.formPills : [];
        msg.aiInstruction = data.ai_instruction || null;
      } else {
        msg.proposedAction = data.proposedAction || null;
        msg.suggestedFollowups = Array.isArray(data.suggestedFollowups) ? data.suggestedFollowups : [];
      }
      return msg;
    },

    /**
     * Sends the active conversation turn. The Chat tab posts to the unified
     * /api/teammate/chat (which can run activities and propose modules); the
     * Converse tab posts to /api/teammate/converse (read-only). Both share this
     * UI — only the endpoint and which response fields we surface differ.
     */
    async sendChat() {
      const conv = this.activeChat;
      const isChat = this.activeTab === "chat";
      const endpoint = isChat ? "/api/teammate/chat" : "/api/teammate/converse";

      const text = conv.draft.trim();
      const attachments = conv.attachments.slice();
      if (!text && !attachments.length) return;

      // Snapshot history before appending the new turn (server wants prior turns only).
      const history = this.buildHistory(conv.history);
      conv.draft = "";
      conv.attachments = [];
      this.$nextTick(this.autoGrow);

      const userMsg = { role: "user", content: text, attachments: this.attachmentsPreview(attachments) };
      conv.history.push(userMsg);
      this.scrollChatToEnd();

      const payload = { Message: text, History: history };
      if (this.selectedModuleName) payload.Module = this.selectedModuleName;
      if (conv.entryId) payload.EntryId = conv.entryId;
      if (isChat) payload.Design = !!conv.design;
      // Trace grouping: only /chat reads SessionId server-side; /converse ignores it.
      if (isChat && conv.sessionId) payload.SessionId = conv.sessionId;
      // Wait for staged S3 uploads so the payload carries { name, path } refs.
      if (attachments.length) await this.awaitAttachmentUploads(attachments);
      if (attachments.length) payload.Attachments = this.attachmentsPayload(attachments);
      this.applyModelOverride(payload);

      this.inflight = true;
      this.currentAction = this.activeTab;
      this.progressLabel = null;
      try {
        const res = await commonStore.state.client.post(endpoint, payload, this.requestConfig());
        // Same round-trip as FTSClientTeammate: keep this turn's stored refs on the user
        // bubble so buildHistory replays them and later turns can still attach the files.
        const refs = res.data && Array.isArray(res.data.attachments) ? res.data.attachments : [];
        if (refs.length) this.$set(userMsg, "attachmentRefs", refs);
        conv.history.push(this.buildAssistantMessage(res.data || {}, isChat));
      } catch (err) {
        const msg = (err.response && err.response.data && (err.response.data.message || err.response.data.error))
          || err.message || "Request failed";
        conv.history.push({ role: "assistant", content: "", error: msg });
        this.handleError(err, this.activeTab);
      } finally {
        this.inflight = false;
        this.currentAction = null;
        this.progressLabel = null;
        this.scrollChatToEnd();
      }
    },

    /**
     * Commits a button-confirmed proposal or pending action. The reviewed payload
     * travels back in `confirmations` (stateless — the server caches nothing), and
     * a pure-confirmation turn carries no message, so the server commits it
     * deterministically without re-invoking the model.
     */
    async confirmItem(item, kind) {
      if (this.inflight || item._status === "committing" || item._status === "done") return;

      let conf;
      if (kind === "module") {
        conf = { type: "module", kind: item.kind || "design", schema: this.proposalSchema(item) };
        if (item.module) conf.module = item.module;
      } else {
        // Pending activities round-trip verbatim (already shaped as a confirmation).
        conf = {};
        Object.keys(item).forEach((k) => { if (k[0] !== "_") conf[k] = item[k]; });
      }

      const conv = this.chat; // confirmations only originate from /chat
      this.$set(item, "_status", "committing");
      this.inflight = true;
      this.currentAction = "chat";
      this.progressLabel = null;
      try {
        const payload = { Confirmations: [conf], History: this.buildHistory(conv.history) };
        if (this.selectedModuleName) payload.Module = this.selectedModuleName;
        if (conv.entryId) payload.EntryId = conv.entryId;
        payload.Design = !!conv.design;
        // Same session id as the propose turn so the whole exchange lands in one log file.
        if (conv.sessionId) payload.SessionId = conv.sessionId;
        this.applyModelOverride(payload);

        const res = await commonStore.state.client.post("/api/teammate/chat", payload, this.requestConfig());
        const data = res.data || {};
        this.$set(item, "_status", "done");

        // A freshly created/applied module should show up in the picker, and we focus
        // it so a follow-up ("insert a sample entry") has a module to act on.
        const moduleAction = (data.actions || []).find(
          (a) => a && a.type === "module" && a.success && !a.error);
        // Stash the committed module on the proposal so its card grows a clickable module chip
        // (proposalModuleChip) — the receipt, in place of a separate "Created module" action card.
        if (moduleAction) {
          this.$set(item, "_committedModule", {
            moduleId: moduleAction.moduleId != null ? moduleAction.moduleId : null,
            name: moduleAction.module || this.proposalName(item),
            kind: moduleAction.kind || item.kind,
          });
        }

        const resultMsg = this.buildAssistantMessage(data, true);
        // Drop the committed module's "Created module" action card — the chip now represents it,
        // so the same result isn't shown twice. A failed commit (error) is kept for retry.
        resultMsg.actions = (resultMsg.actions || []).filter(
          (a) => !(a && a.type === "module" && a.success && !a.error));
        if (this.chatResponseHasContent(resultMsg)) conv.history.push(resultMsg);

        if (moduleAction && this.selectedWorkspaceId) {
          await this.loadModules(this.selectedWorkspaceId);
          if (moduleAction.module) this.selectedModuleName = moduleAction.module;
        }
      } catch (err) {
        this.$set(item, "_status", "error");
        const msg = (err.response && err.response.data && (err.response.data.message || err.response.data.error))
          || err.message || "Request failed";
        conv.history.push({ role: "assistant", content: "", error: msg });
        this.handleError(err, "chat:confirm");
      } finally {
        this.inflight = false;
        this.currentAction = null;
        this.progressLabel = null;
        this.scrollChatToEnd();
      }
    },

    /** ----- chat proposal (design/modify) helpers ----- */
    proposalSchema(p) {
      return (p && (p.schema || p.proposedSchema)) || {};
    },
    proposalName(p) {
      return this.proposalSchema(p).name || "Module";
    },
    proposalValid(p) {
      if (p && p.validation && typeof p.validation.valid === "boolean") return p.validation.valid;
      return !!(p && p.success);
    },
    proposalErrors(p) {
      const e = p && p.validation && p.validation.errors;
      if (!e || (Array.isArray(e) && !e.length)) return "";
      return typeof e === "string" ? e : this.prettyJson(e);
    },
    proposalCount(p, key) {
      const arr = this.proposalSchema(p)[key];
      return Array.isArray(arr) ? arr.length : 0;
    },
    /** Button label for a proposal's Create/Apply action, reflecting commit state. */
    proposalCommitLabel(p) {
      const modify = p && p.kind === "modify";
      if (p && p._status === "done") return modify ? "Applied" : "Created";
      return modify ? "Apply changes" : "Create module";
    },
    /** Document/entry reference line for an entry submission card (modules show none). */
    actionSubId(act) {
      if (!act || act.type === "module") return "";
      const parts = [];
      if (act.documentId) parts.push(String(act.documentId));
      if (act.entryId != null) parts.push(`entry ${act.entryId}`);
      return parts.join(" · ");
    },
    /** The submitted field values (keyed by display name) shown on an entry card. */
    actionFields(act) {
      const input = act && act.input;
      if (!input || typeof input !== "object" || Array.isArray(input)) return [];
      const out = [];
      for (const label of Object.keys(input)) {
        const value = this.formatActionValue(input[label]);
        if (value === "") continue;
        out.push({ label, value });
      }
      return out;
    },
    /** Flattens an activity input value (scalar, file/user/ref object, range, or array) to text. */
    formatActionValue(val) {
      if (val == null) return "";
      if (Array.isArray(val)) {
        return val.map((v) => this.formatActionValue(v)).filter((s) => s !== "").join(", ");
      }
      if (typeof val === "object") {
        if (val.value != null) return String(val.value);
        if (val.name != null) return String(val.name);
        if (val.start != null || val.end != null) {
          return [val.start, val.end].filter((s) => s != null && s !== "").join(" → ");
        }
        try { return JSON.stringify(val); } catch (e) { return String(val); }
      }
      return String(val);
    },
    actionHrefLabel(act) {
      return act && act.type === "module" ? "View module" : "View entry";
    },
    /**
     * The entry's state-aware next activities (from the server's availableActivities),
     * as buttons. Custom workflow activities are surfaced as primary; standard ones
     * (edit/delete/comment/…) as secondary. "view" is dropped — the card already links out.
     */
    availableActions(act) {
      const av = act && act.availableActivities;
      if (!av || act.type === "module") return [];
      const out = [];
      (Array.isArray(av.custom) ? av.custom : []).forEach((name) => {
        if (name) out.push({ label: String(name), token: String(name), primary: true });
      });
      (Array.isArray(av.standard) ? av.standard : []).forEach((id) => {
        if (!id) return;
        const key = String(id).toLowerCase();
        // "view" → the card already links out; "create" isn't a next-step on this entry.
        if (key === "view" || key === "create") return;
        out.push({ label: this.standardActionLabel(key), token: String(id), primary: false });
      });
      return out;
    },
    standardActionLabel(id) {
      const map = {
        edit: "Edit", delete: "Delete", comment: "Comment", duplicate: "Duplicate",
        changestatus: "Change status", manage: "Manage", create: "Create", view: "View",
      };
      const key = String(id).toLowerCase();
      return map[key] || (id.charAt(0).toUpperCase() + id.slice(1));
    },
    /** Clicking an action button asks the agent to run that activity on the entry. */
    triggerAction(act, action) {
      if (this.inflight) return;
      const parts = [];
      if (act.documentId) parts.push(String(act.documentId));
      if (act.entryId != null) parts.push(`entry ${act.entryId}`);
      const ref = parts.join(", ") || "this entry";
      const mod = act.module ? ` in "${act.module}"` : "";
      this.activeChat.draft = `Run the "${action.token}" activity on ${ref}${mod}.`;
      this.sendChat();
    },

    async sendExecute() {
      this.execute.contextError = "";
      let context = null;
      try { context = this.parseJson(this.execute.contextStr, null); }
      catch (e) { this.execute.contextError = "Invalid JSON: " + e.message; return; }

      const payload = {
        Module: this.selectedModuleName,
        Instruction: this.execute.instruction,
      };
      if (this.execute.entryId) payload.EntryId = this.execute.entryId;
      if (context) payload.Context = context;
      if (this.execute.attachments.length) await this.awaitAttachmentUploads(this.execute.attachments);
      if (this.execute.attachments.length) payload.Attachments = this.attachmentsPayload(this.execute.attachments);
      this.applyModelOverride(payload);

      this.inflight = true;
      this.currentAction = "execute";
      try {
        const res = await commonStore.state.client.post("/api/teammate/execute", payload, this.requestConfig());
        this.setResponse({
          status: res.status,
          ok: true,
          data: res.data,
          message: (res.data && res.data.message) || "",
        });
      } catch (err) {
        this.handleError(err, "execute");
      } finally {
        this.inflight = false;
        this.currentAction = null;
      }
    },

    async sendChoreograph() {
      const c = this.choreograph;
      const payload = {
        Module: this.selectedModuleName,
        EntryId: c.entryId,
        Trigger: c.trigger || "user_initiated",
        ai_instruction: c.aiInstruction,
      };
      if (c.maxDepth) payload.MaxDepth = c.maxDepth;
      if (c.attachments.length) await this.awaitAttachmentUploads(c.attachments);
      if (c.attachments.length) payload.Attachments = this.attachmentsPayload(c.attachments);

      this.inflight = true;
      this.currentAction = "choreograph";
      try {
        const res = await commonStore.state.client.post("/api/teammate/choreograph", payload, this.requestConfig());
        const data = res.data || {};
        c.runId = data.runId || "";
        c.runStatus = data.status || "";
        this.setResponse({ status: res.status, ok: true, data, message: data.message || "" });
        if (c.autoPoll && c.runId) this.startAutoPoll();
      } catch (err) {
        this.handleError(err, "choreograph");
      } finally {
        this.inflight = false;
        this.currentAction = null;
      }
    },

    async pollChoreography(runId) {
      if (!runId) return;
      try {
        const res = await commonStore.state.client.get(`/api/teammate/choreograph/${encodeURIComponent(runId)}`, this.requestConfig());
        const data = res.data || {};
        this.choreograph.runStatus = data.status || "";
        this.setResponse({ status: res.status, ok: true, data, message: data.message || "" });

        const status = (data.status || "").toLowerCase();
        if (this.choreograph.autoPoll && status !== "running") {
          this.stopAutoPoll();
        }
      } catch (err) {
        this.stopAutoPoll();
        this.handleError(err, "choreograph:status");
      }
    },
    toggleAutoPoll() {
      if (this.choreograph.autoPoll) this.startAutoPoll();
      else this.stopAutoPoll();
    },
    startAutoPoll() {
      this.stopAutoPoll();
      if (!this.choreograph.runId) return;
      this.choreograph.pollTimer = setInterval(() => {
        this.pollChoreography(this.choreograph.runId);
      }, 2500);
    },
    stopAutoPoll() {
      if (this.choreograph.pollTimer) {
        clearInterval(this.choreograph.pollTimer);
        this.choreograph.pollTimer = null;
      }
    },

    async sendDesign() {
      this.design.contextError = "";
      let context = null;
      try { context = this.parseJson(this.design.contextStr, null); }
      catch (e) { this.design.contextError = "Invalid JSON: " + e.message; return; }

      const payload = {
        Description: this.design.description,
        AutoCreate: !!this.design.autoCreate,
      };
      if (context) payload.Context = context;
      if (this.design.attachments.length) await this.awaitAttachmentUploads(this.design.attachments);
      if (this.design.attachments.length) payload.Attachments = this.attachmentsPayload(this.design.attachments);

      this.inflight = true;
      this.currentAction = "design";
      try {
        const res = await commonStore.state.client.post("/api/teammate/design", payload, this.requestConfig());
        this.setResponse({
          status: res.status,
          ok: true,
          data: res.data,
          message: (res.data && res.data.message) || "",
        });
      } catch (err) {
        this.handleError(err, "design");
      } finally {
        this.inflight = false;
        this.currentAction = null;
      }
    },

    async sendModify() {
      const payload = {
        Module: this.selectedModuleName,
        Instruction: this.modify.instruction,
        AutoApply: !!this.modify.autoApply,
      };
      if (this.modify.attachments.length) await this.awaitAttachmentUploads(this.modify.attachments);
      if (this.modify.attachments.length) payload.Attachments = this.attachmentsPayload(this.modify.attachments);

      this.inflight = true;
      this.currentAction = "modify";
      try {
        const res = await commonStore.state.client.post("/api/teammate/modify", payload, this.requestConfig());
        this.setResponse({
          status: res.status,
          ok: true,
          data: res.data,
          message: (res.data && res.data.message) || "",
        });
      } catch (err) {
        this.handleError(err, "modify");
      } finally {
        this.inflight = false;
        this.currentAction = null;
      }
    },

    async copyResponse() {
      try {
        await navigator.clipboard.writeText(this.response.body || "");
        if (this.$alertSuccess) this.$alertSuccess("Response copied");
      } catch (e) {
        console.warn("Clipboard copy failed", e);
      }
    },
  },
  watch: {
    activeTab() {
      // The Chat and Converse tabs share one composer/list; re-sync height and
      // scroll when switching between them so the active draft renders cleanly.
      if (!this.isChatTab) return;
      this.$nextTick(() => {
        this.autoGrow();
        this.scrollChatToEnd();
      });
    },
    selectedWorkspaceId(newId, oldId) {
      if (newId === oldId) return;
      this.selectedModuleName = "";
      this.modules = [];
      if (newId) this.loadModules(newId);
    },
    wsActiveIndex() {
      if (!this.wsOpen) return;
      this.$nextTick(() => {
        const menu = this.$refs.wsMenu;
        if (!menu) return;
        const active = menu.querySelector(".tm-combo-option--active");
        if (active && active.scrollIntoView) active.scrollIntoView({ block: "nearest" });
      });
    },
    modelOverride(value) {
      try { window.localStorage.setItem("tmModelOverride", value || ""); } catch (e) { /* ignore */ }
    },
  },
  created() {
    this.wsSearchSeq = 0;
    this.debouncedWsSearch = (term) => {
      if (this.wsSearchTimer) clearTimeout(this.wsSearchTimer);
      this.wsSearchTimer = setTimeout(() => this.searchWorkspaces(term), 300);
    };
  },
  async mounted() {
    document.addEventListener("click", this.onDocumentClick);
    // Live mid-turn progress over SignalR: subscribe this chat session's group and listen for
    // its frames. Best-effort — if the hub isn't connected the chat still works, just without
    // the per-step status line.
    window.eventBus.$on("signalr.teammateProgress", this.onTeammateProgress);
    signalr.joinSession(this.chat.sessionId);
    this.loadMeta(); // fire-and-forget: only gates the model picker
    await this.loadWorkspaces();
    const preferred = this.initialWorkspaceId();
    let chosen = null;
    if (preferred) {
      chosen = this.workspaces.find((w) => String(w.id) === String(preferred)) || null;
    }
    if (!chosen && this.workspaces[0] && this.workspaces[0].id) {
      chosen = this.workspaces[0];
    }
    if (chosen) {
      this.selectedWorkspaceId = chosen.id;
      this.selectedWorkspaceLabel = chosen.name || String(chosen.id);
    }
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onDocumentClick);
    window.eventBus.$off("signalr.teammateProgress", this.onTeammateProgress);
    signalr.leaveSession(this.chat.sessionId);
    if (this.wsSearchTimer) clearTimeout(this.wsSearchTimer);
    this.stopAutoPoll();
  },
};
</script>

<style scoped>
.fts-teammate {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #fafbfc;
  color: #1f2328;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 14px;
}

.tm-header {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 20px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}
.tm-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.tm-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 15px;
}
.tm-tag {
  font-size: 11px;
  color: #6b7280;
  background: #f3f4f6;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 500;
}
.tm-pickers {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.tm-picker {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 4px 8px 4px 10px;
}
.tm-picker-icon {
  color: #2b9ec6;
  font-size: 12px;
}
.tm-picker-select {
  border: none;
  background: transparent;
  font-size: 12px;
  font-weight: 500;
  color: #1f2328;
  padding: 2px 4px;
  min-width: 140px;
  max-width: 220px;
  cursor: pointer;
}
.tm-picker-select:focus { outline: none; }
.tm-picker-select:disabled { color: #9ca3af; cursor: not-allowed; }
.tm-picker--model .tm-picker-select {
  min-width: 220px;
  font-family: ui-monospace, SFMono-Regular, Consolas, monospace;
  font-size: 12px;
}
.tm-picker-spin {
  color: #2b9ec6;
  font-size: 11px;
}
/* ----- searchable workspace combobox ----- */
.tm-combo-input {
  cursor: text;
}
.tm-combo-input::placeholder {
  color: #9ca3af;
  font-weight: 500;
}
.tm-combo-caret {
  color: #9ca3af;
  font-size: 11px;
  cursor: pointer;
  padding: 2px;
}
.tm-combo-caret:hover { color: #6b7280; }
.tm-combo-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 50;
  margin: 0;
  padding: 4px;
  list-style: none;
  min-width: 100%;
  max-width: 320px;
  max-height: 260px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(17, 24, 39, 0.12);
}
.tm-combo-option {
  padding: 6px 10px;
  font-size: 12px;
  color: #1f2328;
  border-radius: 6px;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tm-combo-option--active { background: #f3f4f6; }
.tm-combo-option--selected { color: #2b9ec6; font-weight: 600; }
.tm-combo-option--selected.tm-combo-option--active { background: #d3ecf5; }
.tm-combo-empty {
  padding: 8px 10px;
  font-size: 12px;
  color: #9ca3af;
  font-style: italic;
}
.tm-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fcd34d;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 12px;
  margin-bottom: 12px;
  max-width: 880px;
}
.tm-tabs {
  display: flex;
  gap: 4px;
}
.tm-tab {
  padding: 6px 14px;
  border: 1px solid transparent;
  background: transparent;
  color: #6b7280;
  font-size: 13px;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
}
.tm-tab:hover { background: #f3f4f6; color: #1f2328; }
.tm-tab--active {
  background: #d3ecf5;
  color: #2b9ec6;
}

.tm-body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.tm-pane {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}
.tm-pane--form {
  padding: 20px;
  overflow-y: auto;
}

/* ----- chat ----- */
.tm-chat-config {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  padding: 12px 20px;
  background: #fff;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}
.tm-chat-list {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.tm-empty {
  margin: auto;
  text-align: center;
  color: #9ca3af;
}
.tm-empty-icon {
  font-size: 32px;
  margin-bottom: 12px;
  color: #9fd3e5;
}
.tm-empty p { margin: 4px 0; }
.tm-empty-hint { font-size: 12px; }
.tm-empty code {
  background: #f3f4f6;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 12px;
}

.tm-msg {
  display: flex;
  gap: 12px;
  max-width: 880px;
  width: 100%;
  margin: 0 auto;
}
.tm-msg-avatar {
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  background: #e5e7eb;
  color: #4b5563;
}
.tm-msg--user .tm-msg-avatar { background: #dbeafe; color: #1d4ed8; }
.tm-msg--assistant .tm-msg-avatar { background: #d3ecf5; color: #2b9ec6; }
.tm-msg-body {
  flex: 1 1 auto;
  min-width: 0;
}
.tm-msg-role {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 2px;
}
.tm-msg-content {
  word-wrap: break-word;
  line-height: 1.55;
}
.tm-msg-loading { color: #9ca3af; font-style: italic; }

.tm-msg-attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}
.tm-msg-att-thumb {
  max-width: 160px;
  max-height: 160px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  object-fit: cover;
}
.tm-msg-att-file {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
  font-size: 12px;
  color: #4b5563;
}

.tm-msg-error {
  margin-top: 6px;
  font-size: 12px;
  color: #b91c1c;
  background: #fee2e2;
  padding: 6px 10px;
  border-radius: 6px;
}
.tm-msg-extra {
  margin-top: 8px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 8px 10px;
}
.tm-extra-label {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  margin-bottom: 4px;
}
.tm-msg-extra pre {
  margin: 0;
  font-size: 11px;
  white-space: pre-wrap;
  word-break: break-word;
}
.tm-followups {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}
.tm-followup {
  font-size: 12px;
  padding: 4px 10px;
  border: 1px solid #d1d5db;
  border-radius: 14px;
  background: #fff;
  color: #4b5563;
  cursor: pointer;
}
.tm-followup:hover { background: #f3f4f6; }

/* ----- chat: actions performed ----- */
.tm-action-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.tm-action-item {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 12px;
}
.tm-action-activity {
  font-weight: 600;
  color: #1f2328;
}
.tm-action-meta { color: #6b7280; }
.tm-action-flag { color: #b45309; }
.tm-action-ic { color: #047857; font-size: 11px; }
.tm-action-item.is-error .tm-action-ic { color: #b91c1c; }
.tm-action-item.is-error .tm-action-activity { color: #991b1b; }
.tm-action-err { color: #b91c1c; }

/* ----- chat: submission cards (form entry / module) ----- */
.tm-sub-card {
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 10px;
  padding: 10px 12px;
}
.tm-sub-card--module { border-color: #ddd6fe; background: #faf5ff; }
.tm-sub-head {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}
.tm-sub-icon {
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #d3ecf5;
  color: #2b9ec6;
  font-size: 13px;
}
.tm-sub-headtext {
  flex: 1 1 auto;
  min-width: 0;
}
.tm-sub-title {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
}
.tm-sub-verb {
  font-weight: 600;
  color: #1f2328;
  font-size: 13px;
}
.tm-sub-module {
  font-size: 12.5px;
  color: #6b7280;
}
.tm-sub-subid {
  margin-top: 2px;
  font-size: 11.5px;
  color: #6b7280;
  font-variant-numeric: tabular-nums;
}
.tm-sub-state {
  flex: 0 0 auto;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 9px;
  border-radius: 10px;
  background: #f3f4f6;
  color: #4b5563;
  white-space: nowrap;
}
.tm-sub-flag {
  flex: 0 0 auto;
  color: #b45309;
}
.tm-sub-fields {
  margin: 10px 0 0;
  display: grid;
  grid-template-columns: minmax(96px, max-content) 1fr;
  gap: 5px 14px;
  font-size: 12.5px;
}
.tm-sub-fields dt {
  color: #6b7280;
  word-break: break-word;
}
.tm-sub-fields dd {
  margin: 0;
  color: #1f2328;
  font-weight: 500;
  word-break: break-word;
}
.tm-sub-foot {
  margin-top: 10px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.tm-sub-action {
  font-size: 12px;
  font-weight: 500;
  padding: 5px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  color: #374151;
  cursor: pointer;
}
.tm-sub-action:hover:not(:disabled) {
  background: #f3f4f6;
  border-color: #9fd3e5;
  color: #22809f;
}
.tm-sub-action:disabled { color: #c4c8cf; border-color: #e5e7eb; cursor: not-allowed; }
.tm-sub-action--primary {
  background: #2b9ec6;
  border-color: #2b9ec6;
  color: #fff;
}
.tm-sub-action--primary:hover:not(:disabled) {
  background: #22809f;
  border-color: #22809f;
  color: #fff;
}
.tm-sub-action--primary:disabled { background: #9fd3e5; border-color: #9fd3e5; color: #fff; }
.tm-sub-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  font-size: 12px;
  font-weight: 600;
  color: #2b9ec6;
  text-decoration: none;
}
.tm-sub-link:hover { color: #22809f; }
.tm-sub-link svg { font-size: 10px; }

/* ----- chat: pending actions awaiting confirmation ----- */
.tm-pending {
  background: #fffbeb;
  border-color: #fde68a;
}
.tm-pending-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 0;
  font-size: 12px;
}
.tm-pending-item + .tm-pending-item { border-top: 1px solid #fde68a; }
.tm-pending-info {
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
}
.tm-access-changes {
  flex-basis: 100%;
  margin: 2px 0 0;
  padding-left: 16px;
}
.tm-access-warning { color: #92400e; }
.tm-pending-actor {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 1px 6px;
  border-radius: 8px;
  background: #fef3c7;
  color: #92400e;
}
.tm-pending-actor--hybrid { background: #e6f4f9; color: #3730a3; }

/* shared confirm / commit button */
.tm-confirm-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 5px 12px;
  border: none;
  border-radius: 8px;
  background: #2b9ec6;
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
}
.tm-confirm-btn:hover:not(:disabled) { background: #22809f; }
.tm-confirm-btn:disabled { background: #9fd3e5; cursor: not-allowed; }

/* ----- chat: open-form pills (human / no-actor activities) ----- */
.tm-formpill-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.tm-formpill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 12px;
  border: 1px solid #c7d2fe;
  border-radius: 999px;
  background: #eef2ff;
  color: #3730a3;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
}
.tm-formpill:hover { background: #e6f4f9; border-color: #9fd3e5; }
.tm-formpill-ic { color: #2b9ec6; }
.tm-formpill-activity { font-weight: 600; }

/* ----- chat: design / modify proposals ----- */
.tm-proposals {
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.tm-proposal {
  border: 1px solid #ddd6fe;
  background: #faf5ff;
  border-radius: 8px;
  padding: 10px 12px;
}
.tm-proposal-head {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #2b9ec6;
}
.tm-proposal-name {
  font-weight: 600;
  color: #1f2328;
}
.tm-proposal-phase {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 1px 8px;
  border-radius: 10px;
  background: #d3ecf5;
  color: #22809f;
}
.tm-proposal-valid {
  margin-left: auto;
  font-size: 11px;
  font-weight: 600;
  padding: 1px 8px;
  border-radius: 10px;
}
.tm-proposal-valid.is-valid { background: #d1fae5; color: #065f46; }
.tm-proposal-valid.is-invalid { background: #fee2e2; color: #991b1b; }
.tm-proposal-msg {
  margin-top: 6px;
  font-size: 12.5px;
  color: #4b5563;
}
.tm-proposal-counts {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 11px;
  color: #6b7280;
  font-variant-numeric: tabular-nums;
}
.tm-proposal-errors {
  margin-top: 8px;
  font-size: 11px;
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  padding: 6px 8px;
  white-space: pre-wrap;
  word-break: break-word;
}
.tm-proposal-schema {
  margin-top: 8px;
  font-size: 12px;
}
.tm-proposal-schema summary {
  cursor: pointer;
  color: #2b9ec6;
  font-weight: 500;
}
.tm-proposal-schema pre {
  margin: 6px 0 0;
  max-height: 280px;
  overflow: auto;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 11px;
  white-space: pre-wrap;
  word-break: break-word;
}
.tm-proposal-actions {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.tm-proposal-hint {
  font-size: 11px;
  color: #b45309;
}
/* committed-module chip, opposite the Created button */
.tm-proposal-modulechip {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  min-width: 0;
}

.tm-chat-design {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  color: #2b9ec6;
  cursor: pointer;
  white-space: nowrap;
}
.tm-chat-design input { cursor: pointer; }
.tm-chat-note {
  font-size: 11px;
  color: #6b7280;
  font-style: italic;
}

.tm-msg-usage {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 11px;
  color: #6b7280;
  font-variant-numeric: tabular-nums;
}
.tm-msg-usage .tm-usage-total {
  color: #4b5563;
  font-weight: 500;
}
.tm-msg-usage .tm-msg-cost {
  color: #047857;
  font-weight: 600;
}
.tm-usage-cache {
  color: #b45309;
}
.tm-usage-tools {
  color: #2b9ec6;
}
.tm-usage-latency {
  color: #0e7490;
}
.tm-usage-model {
  color: #2b9ec6;
  font-weight: 600;
}

.tm-session-usage {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  padding: 4px 10px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  font-size: 12px;
  color: #4b5563;
  font-variant-numeric: tabular-nums;
}
.tm-usage-label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
}
.tm-usage-pair {
  display: flex;
  gap: 8px;
}
.tm-usage-pair .tm-usage-total {
  color: #1f2328;
  font-weight: 600;
}
.tm-session-cost {
  color: #047857;
  font-weight: 600;
  padding-left: 8px;
  border-left: 1px solid #e5e7eb;
}

.tm-chat-input {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 20px;
  background: #fff;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
}
.tm-chat-input--drag {
  outline: 2px dashed rgba(43, 158, 198, 0.6);
  outline-offset: -4px;
  border-radius: 8px;
}
.tm-chat-input-row {
  display: flex;
  gap: 10px;
  align-items: flex-end;
}
.tm-btn-attach {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #6b7280;
  cursor: pointer;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
}
.tm-btn-attach:hover:not(:disabled) { background: #f1f9fc; color: #2b9ec6; border-color: #9fd3e5; }
.tm-btn-attach:disabled { opacity: 0.5; cursor: not-allowed; }
.tm-chat-input textarea {
  flex: 1 1 auto;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 14px;
  font-family: inherit;
  resize: none;
  max-height: 180px;
  line-height: 1.45;
}
.tm-chat-input textarea:focus {
  outline: none;
  border-color: #2b9ec6;
  box-shadow: 0 0 0 3px rgba(43, 158, 198, 0.15);
}

/* ----- form ----- */
.tm-form {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 14px 16px;
  max-width: 880px;
}
.tm-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.tm-field-full { grid-column: 1 / -1; }
.tm-field-inline {
  flex-direction: row;
  align-items: center;
  gap: 6px;
}
.tm-field-inline label { margin: 0; }
.tm-field label {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
}
.tm-req { color: #dc2626; }
.tm-field input[type="text"],
.tm-field input[type="number"],
.tm-field textarea {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 13px;
  font-family: inherit;
  background: #fff;
}
.tm-field textarea { resize: vertical; min-height: 60px; }
.tm-mono { font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; }
.tm-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #1f2328;
  font-weight: 500;
}
.tm-field-error {
  font-size: 12px;
  color: #b91c1c;
}
.tm-form-actions {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
  max-width: 880px;
}

/* ----- buttons ----- */
.tm-btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: none;
  background: #2b9ec6;
  color: #fff;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}
.tm-btn-primary:hover:not(:disabled) { background: #22809f; }
.tm-btn-primary:disabled { background: #9fd3e5; cursor: not-allowed; }
.tm-btn-ghost {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #4b5563;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
}
.tm-btn-ghost:hover:not(:disabled) { background: #f3f4f6; }
.tm-btn-ghost:disabled { color: #d1d5db; cursor: not-allowed; }
.tm-btn-xs { padding: 2px 8px; font-size: 11px; }
.tm-btn-send {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: none;
  background: #2b9ec6;
  color: #fff;
  cursor: pointer;
  flex-shrink: 0;
}
.tm-btn-send:hover:not(:disabled) { background: #22809f; }
.tm-btn-send:disabled { background: #9fd3e5; cursor: not-allowed; }

/* ----- runner / response ----- */
.tm-runner {
  margin-top: 16px;
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 8px;
  max-width: 880px;
}
.tm-runner-row {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}
.tm-runner-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  margin-bottom: 2px;
}
.tm-runner-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 12px;
}
.tm-poll-toggle {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #4b5563;
}
.tm-status {
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  background: #f3f4f6;
  color: #4b5563;
}
.tm-status--running { background: #fef3c7; color: #92400e; }
.tm-status--complete,
.tm-status--completed,
.tm-status--success { background: #d1fae5; color: #065f46; }
.tm-status--error,
.tm-status--failed { background: #fee2e2; color: #991b1b; }

.tm-response {
  border-top: 1px solid #e5e7eb;
  background: #1f2937;
  color: #e5e7eb;
  padding: 12px 20px;
  max-height: 280px;
  overflow: auto;
  flex-shrink: 0;
}
.tm-response-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}
.tm-response-title {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  color: #9ca3af;
}
.tm-pill {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
}
.tm-pill--ok { background: #065f46; color: #d1fae5; }
.tm-pill--err { background: #991b1b; color: #fee2e2; }
.tm-pill--usage {
  background: #374151;
  color: #e5e7eb;
  font-variant-numeric: tabular-nums;
}
.tm-response-message {
  margin-bottom: 8px;
  color: #f3f4f6;
  font-size: 13px;
}
.tm-response-body {
  margin: 0;
  background: #111827;
  color: #d1d5db;
  font-size: 11px;
  line-height: 1.5;
  padding: 10px 12px;
  border-radius: 6px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 220px;
}
.tm-response-empty {
  font-size: 12px;
  color: #9ca3af;
  font-style: italic;
}
</style>

<style>
/* Markdown-rendered chat content. Non-scoped because v-html output
   is not affected by scoped data attributes. All selectors are
   prefixed with .tm-md so collisions are impossible. */
.tm-md p { margin: 0 0 8px; }
.tm-md p:last-child { margin-bottom: 0; }
.tm-md ul,
.tm-md ol {
  margin: 4px 0 8px;
  padding-left: 24px;
}
.tm-md ul:last-child,
.tm-md ol:last-child { margin-bottom: 0; }
.tm-md li { margin: 2px 0; }
.tm-md li > p { margin: 0; }
.tm-md strong { font-weight: 600; color: #111827; }
.tm-md em { font-style: italic; }
.tm-md a {
  color: #2b9ec6;
  text-decoration: underline;
}
.tm-md a:hover { color: #22809f; }
.tm-md code {
  background: #f3f4f6;
  padding: 1px 6px;
  border-radius: 4px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12.5px;
  color: #be185d;
}
.tm-md pre {
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  padding: 8px 10px;
  border-radius: 6px;
  margin: 6px 0;
  overflow: auto;
}
.tm-md pre code {
  background: transparent;
  padding: 0;
  color: #1f2328;
  font-size: 12px;
  line-height: 1.5;
}
.tm-md blockquote {
  border-left: 3px solid #d1d5db;
  padding-left: 10px;
  color: #6b7280;
  margin: 6px 0;
}
.tm-md h1,
.tm-md h2,
.tm-md h3,
.tm-md h4,
.tm-md h5,
.tm-md h6 {
  font-weight: 600;
  margin: 10px 0 4px;
  line-height: 1.3;
}
.tm-md h1 { font-size: 18px; }
.tm-md h2 { font-size: 16px; }
.tm-md h3 { font-size: 15px; }
.tm-md h4,
.tm-md h5,
.tm-md h6 { font-size: 14px; }
.tm-md > *:first-child { margin-top: 0; }
</style>
